import React, { useEffect, useState } from "react";
import _ from "lodash";
import moment from "moment";
import { useSelector } from "react-redux";
import momentTZ from "moment-timezone";
import {
  generateAgeByDOB,
  getDurationOptions,
  getStartTimeOptions,
  isInvalidDateInAvailableDates,
  isInvalidTime,
  smsTemplatesSorter,
} from "../../utils";
import TextInputField from "../Shared/TextInputField";
import SelectField from "../Shared/SelectField";
import Textarea from "../Shared/Textarea";
import Button from "../Shared/Button";
import Tooltip from "../Shared/Tooltip";
import { getPractitionerAvailability } from "../../services/practitioner";
import AppointmentTimeslot from "./AppointmentTimeslot";
import PractitionerSelector from "./PractitionerSelector";
import { isActivePractitionerId, selectActivePractitioners } from "../appSlice";
import { getAllSMSTemplates } from "../../services/smsTemplates";
import { newOrReviewOptions, WORKFLOW_NAME } from "../constant";
import DangerTip from "./DangerTip";
import CheckboxField from "../Shared/CheckboxField";
import AvailabilityCalendar from "./AvailabilityCalendar";
import AvailableTimeslots from "./AvailableTimeslots";

import "./AppointmentForm.css";
import { APPOINTMENT_TYPE, DEFAULT_TIMEZONE } from "../../constants";
import { APPOINTMENT_CANCELED_REASON_OPTIONS } from "../constant";
import { isPmsBookingEnabled } from "../../infra/featureToggle";
import { collectColorKeysFromTimeslots } from "../../utils/locationColor";

import { displayDateTime } from "../../utils/dateDisplay";
// The date and time a coordinator types belong to the tenant's clock, not to
// whatever zone their laptop is set to. Built with plain moment() these were
// stamped with the browser's offset: a Melbourne coordinator typing "10:00"
// for a Queensland clinic stored 09:00 Brisbane, and every screen echoed the
// typed 10:00 back so nothing looked wrong.
const typedAppointmentMoment = (date, time) => momentTZ.tz(`${date} ${time}`, "YYYY-MM-DD HH:mm", DEFAULT_TIMEZONE);

// A stored instant, shown in the booking zone so it round-trips through the form.
const storedAppointmentMoment = (value) => momentTZ(value, "YYYY-MM-DDTHH:mm:ss Z").tz(DEFAULT_TIMEZONE);

const zoomAccounts = {
  zoom_admin1: "telecare.zoom@gmail.com",
  zoom_admin2: "admin2@telecareonline.com.au",
  zoom_admin: "admin@telecareonline.com.au",
  zoom_admin4: "admin4@telecareonline.com.au",
};

// ZOOM is left out because the API would create a real Zoom meeting for it,
// and nothing handles SKYPE.
const dialInMethodOptions = [
  { label: "COREPLUS", value: "coreplus" },
  { label: "PHONE", value: "phone" },
];

const contOrTbcOptions = [
  { label: "CONT", value: "CONT" },
  { label: "TBC", value: "TBC" },
];

const { AT_HOME, AT_GP_CLINIC } = APPOINTMENT_TYPE;

const isDataFromPMS = process.env.REACT_APP_DATA_SOURCE === "pms-api";

const AppointmentForm = ({
  workflowName = "WORKFLOW",
  isNewAppointment,
  caseManager,
  appointment = null,
  preConsult,
  isRTNoteFilled,
  selectedPractitioner,
  referralEmail,
  referralExpiredDate,
  client,
  clinic,
  practitioners,
  submit,
  remove,
  change,
  isLoading = false,
  actionName,
  inform,
  clientAppointmentId,
  location,
  URN,
}) => {
  const locationId = location?.locationId;
  const specialistSchedulerDescriptions = useSelector((state) => state.app.specialistSchedulerDescriptions);
  const activePractitioners = useSelector(selectActivePractitioners);

  const [failureReason, setFailureReason] = useState(null);
  const [appointmentType, setAppointmentType] = useState("");
  // Month/year drive the availability request, so they must sit on the same
  // clock as the date field - otherwise an appointment just after midnight
  // shows one month while the picker fetches the other.
  const initialAppointmentMoment = _.has(appointment, "startDateTime")
    ? storedAppointmentMoment(_.get(appointment, "startDateTime"))
    : momentTZ().tz(DEFAULT_TIMEZONE);
  const [month, setMonth] = useState(initialAppointmentMoment.format("MM"));
  const [year, setYear] = useState(initialAppointmentMoment.format("YYYY"));
  const [appointmentDate, setAppointmentDate] = useState("");
  const [appointmentStartTime, setAppointmentStartTime] = useState("");
  const [practitionerId, setPractitionerId] = useState("");
  const [notes, setNotes] = useState("");
  const [gpNotes, setGpNotes] = useState("");
  const [duration, setDuration] = useState("");
  const [templateSets, setTemplateSets] = useState([]);
  const [selectedSMSTemplateSetId, setSelectedSMSTemplateSetId] = useState("");
  const [dialInMethod, setDialInMethod] = useState("coreplus");
  const [contOrTbc, setContOrTbc] = useState("CONT");
  const [sendCancellationEmail, setSendCancellationEmail] = useState(false);
  const [newOrReview, setNewOrReview] = useState("");
  const [selectedPractitionerAvailabilities, setSelectedPractitionerAvailabilities] = useState([]);
  const [selectedDateAvailability, setSelectedDateAvailability] = useState(null);
  const [selectedSlotId, setSelectedSlotId] = useState("");
  const [skipInitialSMS, setSkipInitialSMS] = useState(false);
  const [appointmentCanceledReason, setAppointmentCanceledReason] = useState("");

  const preConsultNote = preConsult?.notes;
  const isAppointmentExistAndIsNotAPTRequests = workflowName !== WORKFLOW_NAME.APT_REQUESTS && !_.isNull(appointment);
  const isAppointmentNotExistAndNotAPTRequests = workflowName !== WORKFLOW_NAME.APT_REQUESTS && _.isNull(appointment);
  const isAPTRequestAndAppointmentExist = workflowName === WORKFLOW_NAME.APT_REQUESTS && !_.isNull(appointment);
  const {
    dateOfBirth: clientDOB = "",
    clientId = "",
    firstName = "",
    lastName = "",
    clientName = `${firstName} ${lastName}`,
  } = client;
  const schedule = _.get(specialistSchedulerDescriptions, [practitionerId, "value"], "");

  const isCancelAppointmentReasonRequired =
    isDataFromPMS &&
    isAppointmentExistAndIsNotAPTRequests &&
    actionName === "add_cancel_appointment" &&
    !_.isNull(appointment);

  useEffect(() => {
    setNewOrReview(isNewAppointment ? newOrReviewOptions[0].value : newOrReviewOptions[1].value);
  }, [isNewAppointment]);

  useEffect(() => {
    if (!practitionerId) {
      setDuration("");
    }
    if (!duration && newOrReview && practitionerId) {
      const practitioner = practitioners.find(({ practitionerId: pid }) => pid === practitionerId);
      const nextDuration = _.get(practitioner, ["appointmentLength", `${newOrReview.toLowerCase()}Time`], "");
      if (nextDuration) {
        setDuration(nextDuration);
      }
    }
  }, [duration, newOrReview, practitionerId, practitioners]);

  useEffect(() => {
    // An existing appointment keeps its stored practitioner.
    if (isAppointmentExistAndIsNotAPTRequests) return;
    const caseManagerId = _.get(caseManager, "practitionerId", null);
    // A case manager Sync has deactivated is not offered for a new booking.
    if (_.isNull(caseManagerId) || !isActivePractitionerId(activePractitioners, caseManagerId)) return;
    // Only fills an empty choice, so a refresh of the list never replaces a pick.
    setPractitionerId((current) => current || caseManagerId);
  }, [caseManager, activePractitioners, isAppointmentExistAndIsNotAPTRequests]);

  useEffect(() => {
    getAllSMSTemplates({ type: "appointment_auto_sms" }).then((response) => {
      const sortedTemplates = response.result.sort(smsTemplatesSorter);
      setTemplateSets(sortedTemplates);
    });
  }, [appointment, isAppointmentNotExistAndNotAPTRequests]);

  useEffect(() => {
    setSelectedSMSTemplateSetId(
      _.get(appointment, "selectedSMSTemplateSetId", "") ||
        templateSets.find(({ name }) => name === location?.name)?.id ||
        ""
    );
    if (isAppointmentNotExistAndNotAPTRequests) {
      return;
    }
    const startDateTime = _.get(appointment, "startDateTime", "");
    const endDateTime = _.get(appointment, "endDateTime", "");
    setAppointmentDate(storedAppointmentMoment(startDateTime).format("YYYY-MM-DD"));
    setAppointmentStartTime(storedAppointmentMoment(startDateTime).format("HH:mm"));
    setDuration(
      moment(endDateTime, "YYYY-MM-DDTHH:mm:ss Z")
        .diff(moment(startDateTime, "YYYY-MM-DDTHH:mm:ss Z"), "minutes")
        .toString()
    );
    setPractitionerId(_.get(appointment, "practitioner.practitionerId", ""));
    setGpNotes(_.get(appointment, "gpNotes", ""));
    setNotes(_.get(appointment, "notes", ""));
    setAppointmentType(_.get(appointment, "appointmentType", AT_GP_CLINIC));
  }, [appointment, isAppointmentNotExistAndNotAPTRequests, location?.name, templateSets]);

  useEffect(() => {
    if (isAppointmentExistAndIsNotAPTRequests) {
      return;
    }
    const autoNotes = [];
    if (!_.isEmpty(URN)) {
      autoNotes.push(`URN ${URN}`);
    }
    if (!_.isEmpty(location?.name)) {
      autoNotes.push(location.name);
    }
    if (!_.isEmpty(dialInMethod)) {
      autoNotes.push(_.toUpper(dialInMethod));
    }
    if (!_.isEmpty(contOrTbc)) {
      autoNotes.push(`${momentTZ().tz(DEFAULT_TIMEZONE).format("DD MMM")} ${contOrTbc}`);
    }
    if (!_.isEmpty(newOrReview)) {
      autoNotes.push(newOrReview);
    }
    let finalNotes = autoNotes.join(" - ");
    if (!_.isEmpty(gpNotes)) {
      finalNotes += "\n" + gpNotes;
    }
    setNotes(finalNotes);
  }, [
    location,
    dialInMethod,
    contOrTbc,
    newOrReview,
    clinic,
    appointmentType,
    appointment,
    isNewAppointment,
    selectedSMSTemplateSetId,
    isAppointmentExistAndIsNotAPTRequests,
    gpNotes,
    preConsultNote,
    URN,
  ]);

  useEffect(() => {
    if (_.isEmpty(practitionerId)) {
      return;
    }

    let cancelled = false;

    (async () => {
      const practitionerAvailabilities = await getPractitionerAvailability(
        practitionerId,
        `${year}${month}`,
        newOrReview,
        clientAppointmentId,
        // Duration is not sent: the length now comes from each PMS window, and
        // refetching whenever it changes only repeated the same request.
        isPmsBookingEnabled ? { locationId } : {}
      );
      if (!cancelled) {
        setSelectedPractitionerAvailabilities(practitionerAvailabilities);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [practitionerId, year, month, newOrReview, clientAppointmentId, locationId]);

  const findMatchingAvailabilitySlot = (dayAvailability, startTime, appointmentDuration) => {
    if (!dayAvailability?.availableTimeslots?.length || _.isEmpty(startTime)) {
      return undefined;
    }
    return dayAvailability.availableTimeslots.find(({ start, end }) => {
      if (_.isEmpty(appointmentDuration)) {
        return startTime >= start && startTime < end;
      }
      const endTime = moment(startTime, "HH:mm").add(Number(appointmentDuration), "minutes").format("HH:mm");
      return startTime >= start && endTime <= end;
    });
  };

  // PMS's appointment length for the searched type, on the free window holding
  // `startTime`. It comes from the specialist's schedule for that day, so it
  // follows a length override set in OPAU; 0 when PMS does not say.
  const findSlotAppointmentLength = (dayAvailability, startTime) => {
    if (!dayAvailability?.availableTimeslots?.length || _.isEmpty(startTime)) {
      return 0;
    }
    const window = dayAvailability.availableTimeslots.find(({ start, end }) => startTime >= start && startTime < end);
    return Number(_.get(window, "durationMinutes", 0)) || 0;
  };

  const isChoosingSlot =
    _.isNull(appointment) || workflowName === WORKFLOW_NAME.APT_REQUESTS || actionName === "change_appointment";

  const slotAppointmentLength =
    isPmsBookingEnabled && isChoosingSlot
      ? findSlotAppointmentLength(
          selectedPractitionerAvailabilities.find(({ date }) => date === appointmentDate),
          appointmentStartTime
        )
      : 0;

  // PMS lengths are multiples of 5 but may fall outside the fixed list (e.g. 75).
  // The current duration is kept as an option too, so the select still shows it
  // after the start time moves out of the PMS window that set it.
  const baseDurationOptions = getDurationOptions();
  const extraDurations = _.uniq([slotAppointmentLength, Number(duration)]).filter(
    (minutes) => minutes > 0 && !baseDurationOptions.some(({ value }) => Number(value) === minutes)
  );
  const durationOptions = _.isEmpty(extraDurations)
    ? baseDurationOptions
    : _.sortBy(
        [...baseDurationOptions, ...extraDurations.map((minutes) => ({ label: `${minutes} mins`, value: minutes }))],
        "value"
      );

  useEffect(() => {
    if (slotAppointmentLength > 0 && String(slotAppointmentLength) !== String(duration)) {
      setDuration(String(slotAppointmentLength));
    }
  }, [slotAppointmentLength, duration]);

  useEffect(() => {
    if (!isPmsBookingEnabled) {
      return;
    }
    if (_.isEmpty(appointmentDate) || _.isEmpty(appointmentStartTime)) {
      setSelectedSlotId("");
      return;
    }
    const dayAvailability = selectedPractitionerAvailabilities.find(({ date }) => date === appointmentDate);
    const matchedSlot = findMatchingAvailabilitySlot(dayAvailability, appointmentStartTime, duration);
    setSelectedSlotId(matchedSlot?.slotId || "");
  }, [appointmentDate, appointmentStartTime, duration, selectedPractitionerAvailabilities]);

  const handleChangePractitioner = async (id) => {
    setPractitionerId(id);
  };

  const handleChangeAppointmentDate = async (aptDate) => {
    setAppointmentDate(aptDate);
  };

  const handleChangeAppointmentStartTime = (startTime) => {
    setAppointmentStartTime(startTime);
  };

  const handleChangeDuration = (nextDuration) => {
    setDuration(nextDuration);
  };

  const validateAppointment = () => {
    const availableTimes = selectedPractitionerAvailabilities.map(({ availableTimeslots, date }) => {
      return {
        availableTimeslots,
        date,
      };
    });

    // For indefinitely valid referral, Coreplus will return 1900-01-01T00:00:00 as the end date
    if (
      referralExpiredDate?.diff(moment(), "day", true) <= 0 &&
      referralExpiredDate?.unix() !== moment("1900-01-01T00:00:00").unix()
    ) {
      setFailureReason("The referral is expired.");
      return false;
    }

    if (
      isInvalidDateInAvailableDates(
        appointmentDate,
        availableTimes.map(({ date }) => date)
      )
    ) {
      setFailureReason("The time is not available.");
      return false;
    }

    if (_.isEmpty(duration)) {
      setFailureReason("The duration can not be empty.");
      return false;
    }

    const availableTimeslots = availableTimes.find(({ date }) => date === appointmentDate)?.availableTimeslots;

    if (_.isEmpty(availableTimeslots)) {
      setFailureReason("There is no available timeslots.");
      return false;
    }

    if (
      isInvalidTime({
        timeStr: appointmentStartTime,
        duration,
        availableTimes: availableTimeslots,
      })
    ) {
      setFailureReason("The time is not available.");
      return false;
    }

    if (typedAppointmentMoment(appointmentDate, appointmentStartTime).unix() < moment().unix()) {
      setFailureReason("The appointment can not happen in the past.");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFailureReason(null);

    if (!selectedSMSTemplateSetId) {
      setFailureReason("The sms template can not be empty.");
      return;
    }

    const isValidateAppointment = validateAppointment();

    if (!isValidateAppointment) {
      return;
    }

    if (_.isEmpty(practitionerId)) {
      setFailureReason("Please select a valid practitioner.");
      return;
    }

    // A new booking needs an active practitioner; an existing appointment keeps its own.
    if (!isAppointmentExistAndIsNotAPTRequests && !isActivePractitionerId(activePractitioners, practitionerId)) {
      setFailureReason("This practitioner is no longer active. Please select another practitioner.");
      return;
    }

    if (_.isEmpty(appointmentType)) {
      setFailureReason("Please select a valid appointment type.");
      return;
    }

    if (_.isEmpty(dialInMethod)) {
      setFailureReason("Please select a dial-in method.");
      return;
    }

    if (_.isEmpty(locationId)) {
      setFailureReason("Please select a site first.");
      return;
    }

    if (isPmsBookingEnabled && _.isEmpty(selectedSlotId)) {
      setFailureReason("Please select a valid available timeslot.");
      return;
    }

    const start = typedAppointmentMoment(appointmentDate, appointmentStartTime);

    let apt = {
      startDateTime: start.format("YYYY-MM-DDTHH:mm:ssZ"),
      duration,
      endDateTime: start.clone().add(duration, "minutes").format("YYYY-MM-DDTHH:mm:ssZ"),
      practitioner: { practitionerId },
      // The zone the wall time above was read in. Recorded so a later reader
      // does not have to guess; nothing on the API side depends on it yet.
      timezone: DEFAULT_TIMEZONE,
      notes,
      clients: [{ clientId }],
      appointmentTypeId: process.env.REACT_APP_APPOINTMENT_TYPE_ID,
      appointmentType,
      selectedSMSTemplateSetId,
      dialInMethod,
      locationId,
      consultationType: newOrReview,
    };

    if (isPmsBookingEnabled) {
      apt.slotId = selectedSlotId;
    }

    const error = await submit(apt, skipInitialSMS);
    if (error) {
      setFailureReason(_.get(error, "response.data.message") || _.get(error, "response.data.error") || error.message);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    setFailureReason(null);

    const isValidateAppointment = validateAppointment();

    if (!isValidateAppointment) {
      return;
    }

    if (isPmsBookingEnabled && _.isEmpty(selectedSlotId)) {
      setFailureReason("Please select a valid available timeslot.");
      return;
    }

    const data = {
      to: {
        referrer: referralEmail,
        practitioner: selectedPractitioner.email,
      },
      content: {
        specialistName: `${selectedPractitioner.firstName} ${selectedPractitioner.lastName}`,
        patientName: clientName,
        appointmentTime: appointment.startDateTime,
      },
      appointment,
    };
    if (sendCancellationEmail) {
      inform(data);
    }

    const start = typedAppointmentMoment(appointmentDate, appointmentStartTime);
    const startDateTime = start.format("YYYY-MM-DDTHH:mm:ssZ");
    const endDateTime = start.clone().add(duration, "minutes").format("YYYY-MM-DDTHH:mm:ssZ");

    change(
      appointment.appointmentId,
      startDateTime,
      endDateTime,
      duration,
      skipInitialSMS,
      isPmsBookingEnabled ? { slotId: selectedSlotId, consultationType: newOrReview } : undefined
    );
  };

  const handleDelete = (e) => {
    e.preventDefault();
    if (isCancelAppointmentReasonRequired && !appointmentCanceledReason) {
      setFailureReason("The reason for cancellation is required.");
      return;
    }
    const data = {
      to: {
        referrer: referralEmail,
        practitioner: selectedPractitioner.email,
      },
      content: {
        specialistName: `${selectedPractitioner.firstName} ${selectedPractitioner.lastName}`,
        patientName: clientName,
        appointmentTime: appointment.startDateTime,
      },
      appointment,
    };
    if (sendCancellationEmail) {
      inform(data);
    }
    remove(appointment.appointmentId, appointmentCanceledReason);
  };

  const handleAppointmentTypeChange = (e) => {
    const type = e.target.value;
    setAppointmentType(type);
  };

  const handleSelectAvailabilityCalendarDate = (dateKey) => {
    const dayAvailability = selectedPractitionerAvailabilities.find(({ date }) => date.replace(/-/g, "") === dateKey);
    if (!dayAvailability) {
      setSelectedDateAvailability(null);
      return;
    }
    setSelectedDateAvailability({
      full_date: dayAvailability.full_date || dayAvailability.date || moment(dateKey, "YYYYMMDD").format("YYYY-MM-DD"),
      timezone: dayAvailability.timezone || DEFAULT_TIMEZONE,
      timeslots: dayAvailability.timeslots || dayAvailability.availableTimeslots || [],
    });
  };

  // The abbreviation follows the selected date, not today: in a daylight-saving
  // tenant a summer appointment booked in winter is stored at AEDT, and the
  // label must say so.
  const startTimeZoneLabel = (appointmentDate && appointmentStartTime
    ? typedAppointmentMoment(appointmentDate, appointmentStartTime)
    : momentTZ().tz(DEFAULT_TIMEZONE)
  ).format("z");

  let practitioner = null;
  if (isAppointmentExistAndIsNotAPTRequests) {
    const pId = _.get(appointment, "practitioner.practitionerId", "");
    practitioner = practitioners.find((p) => p.practitionerId === pId);
  }

  function getSMSTemplatesOptions() {
    return templateSets
      .map(({ name, id }) => {
        if (name === "RT" && !isRTNoteFilled) {
          return null;
        }

        return { label: name, value: id };
      })
      .filter((set) => !_.isEmpty(set));
  }

  const isCreateAppointment = isAppointmentNotExistAndNotAPTRequests || isAPTRequestAndAppointmentExist;

  return (
    <>
      {referralExpiredDate?.diff(moment(), "month", true) < 3 &&
        moment().isBefore(referralExpiredDate) &&
        referralExpiredDate?.unix() !== moment("1900-01-01T00:00:00").unix() && (
          <DangerTip
            message={`This referral is going to be expired in less 3 months (on ${displayDateTime(
              referralExpiredDate,
              "YYYY-MM-DD HH:mm"
            )}), please make sure the appointment will happened before that or arrange a new referral.`}
            style={`is-warning`}
          />
        )}
      {moment().isAfter(referralExpiredDate) &&
        referralExpiredDate?.unix() !== moment("1900-01-01T00:00:00").unix() && (
          <DangerTip
            message={`This referral has expired (on ${displayDateTime(
              referralExpiredDate,
              "YYYY-MM-DD HH:mm"
            )}), please make sure new referral is available to rebook the appointment`}
          />
        )}
      {generateAgeByDOB(clientDOB, "YYYY-MM-DD") < 17 && <DangerTip message={`This patient is under 17 years old.`} />}
      {isAppointmentExistAndIsNotAPTRequests && (
        <>
          <p className="menu-label">Appointment</p>
          <div className="columns">
            <div className="column">
              Reference number: <strong>{appointment.appointmentReferenceNumber}</strong>
            </div>
          </div>
        </>
      )}
      <div className="columns">
        <div
          className={`column${
            isAppointmentNotExistAndNotAPTRequests ||
            isAPTRequestAndAppointmentExist ||
            actionName === "change_appointment"
              ? " is-three-quarters"
              : ""
          }`}
        >
          <div className="columns">
            <div className="column is-half">
              {_.isNull(practitioner) ? (
                <PractitionerSelector caseManager={caseManager} selectPractitioner={handleChangePractitioner} />
              ) : (
                `Specialist: ${practitioner?.firstName} ${practitioner?.lastName}`
              )}
            </div>
            <div className="column is-half">
              <p className="label is-small">Appointment Types</p>
              <div className="control is-size-7 apt-type-radio-group">
                <label className="radio apt-type-radio">
                  <input
                    value={AT_HOME}
                    type="radio"
                    name="appointmentType"
                    checked={appointmentType === AT_HOME}
                    onChange={handleAppointmentTypeChange}
                    disabled={isAppointmentExistAndIsNotAPTRequests}
                  />
                  <span className={_.isEmpty(appointmentType) ? "has-text-danger" : ""}>{" Own Device"}</span>
                </label>
                <br />
                <label className="radio apt-type-radio">
                  <input
                    value={AT_GP_CLINIC}
                    type="radio"
                    name="appointmentType"
                    checked={appointmentType === AT_GP_CLINIC}
                    onChange={handleAppointmentTypeChange}
                    disabled={isAppointmentExistAndIsNotAPTRequests}
                  />
                  <span className={_.isEmpty(appointmentType) ? "has-text-danger" : ""}>{" Hospital"}</span>
                </label>
                <br />
              </div>
              <div className="control is-size-7">
                <SelectField
                  label="Auto SMS Template"
                  isRequired={true}
                  value={selectedSMSTemplateSetId}
                  disabled={isAppointmentExistAndIsNotAPTRequests}
                  onChange={(e) => setSelectedSMSTemplateSetId(e.target.value)}
                  options={getSMSTemplatesOptions()}
                />
              </div>
            </div>
          </div>
          {!_.isEmpty(practitionerId) && (
            <div className="columns is-variable is-1">
              <div className="column">
                <p>{schedule}</p>
              </div>
            </div>
          )}
          {!_.isEmpty(appointment?.zoom?.joinURL) && (
            <div className="columns is-variable is-1">
              <div className="column is-half">
                <p className="menu-label">ZOOM meeting URL:</p>
                <p>{appointment?.zoom?.joinURL}</p>
                <p className="menu-label">ZOOM Account:</p>
                <p>{zoomAccounts[`${appointment?.zoom?.account}`]}</p>
              </div>
            </div>
          )}
          <div className="columns">
            <div className="column">
              {_.isNull(appointment) ||
              workflowName === WORKFLOW_NAME.APT_REQUESTS ||
              actionName === "change_appointment" ? (
                <AvailabilityCalendar
                  availableDates={selectedPractitionerAvailabilities
                    .filter(({ isValid }) => isValid)
                    .map(({ date, availableTimeslots }) => ({
                      date: date.replace(/-/g, ""),
                      selectedRooms: collectColorKeysFromTimeslots(availableTimeslots),
                    }))}
                  selectDate={handleSelectAvailabilityCalendarDate}
                  handleChangeDate={handleChangeAppointmentDate}
                  month={month}
                  setMonth={setMonth}
                  year={year}
                  setYear={setYear}
                />
              ) : null}
            </div>
            <div className="column">
              <AvailableTimeslots availability={selectedDateAvailability} />
            </div>
          </div>

          <div className="columns is-variable is-1">
            <div className="column">
              <TextInputField
                label="Date"
                isRequired={true}
                hasError={isInvalidDateInAvailableDates(
                  appointmentDate,
                  selectedPractitionerAvailabilities.map(({ date }) => date)
                )}
                value={appointmentDate}
                disabled={true}
                placeholder={momentTZ().tz(DEFAULT_TIMEZONE).format("YYYY-MM-DD")}
                type="date"
              />
            </div>
            <div className="column">
              <SelectField
                label={`Start time (${startTimeZoneLabel})`}
                isRequired={true}
                value={appointmentStartTime}
                disabled={isAppointmentExistAndIsNotAPTRequests && actionName === "add_cancel_appointment"}
                onChange={(e) => handleChangeAppointmentStartTime(e.target.value)}
                options={getStartTimeOptions()}
              />
            </div>
            <div className="column">
              <SelectField
                label="Duration"
                isRequired={true}
                value={duration}
                disabled={
                  (isAppointmentExistAndIsNotAPTRequests && actionName === "add_cancel_appointment") ||
                  slotAppointmentLength > 0
                }
                onChange={(e) => handleChangeDuration(e.target.value)}
                options={durationOptions}
              />
              {slotAppointmentLength > 0 && <p className="help">Set by the specialist&apos;s schedule in PMS.</p>}
            </div>
          </div>
          {(isAppointmentNotExistAndNotAPTRequests || isAPTRequestAndAppointmentExist) && (
            <>
              <div className="columns is-variable is-1">
                <div className="column">
                  <SelectField
                    label="Dial-in Method"
                    isRequired={true}
                    value={dialInMethod}
                    disabled={isAppointmentExistAndIsNotAPTRequests}
                    onChange={(e) => setDialInMethod(e.target.value)}
                    options={dialInMethodOptions}
                  />
                </div>
                <div className="column">
                  <SelectField
                    label="CONT or TBC"
                    value={contOrTbc}
                    disabled={isAppointmentExistAndIsNotAPTRequests}
                    onChange={(e) => setContOrTbc(e.target.value)}
                    options={contOrTbcOptions}
                  />
                </div>
                <div className="column">
                  <SelectField
                    label="NEW or Review"
                    value={newOrReview}
                    disabled={isAppointmentExistAndIsNotAPTRequests}
                    onChange={(e) => setNewOrReview(e.target.value)}
                    options={newOrReviewOptions}
                  />
                </div>
              </div>
            </>
          )}
          <div className="columns is-variable is-1">
            <div className="column">
              <Textarea
                value={notes}
                disabled={isAppointmentExistAndIsNotAPTRequests}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </div>
          {!_.isNull(appointment) && workflowName !== WORKFLOW_NAME.APT_REQUESTS && (
            <div className="columns is-variable is-1">
              <div className="column">
                <CheckboxField
                  label="send cancellation email"
                  checked={sendCancellationEmail}
                  onChange={(e) => {
                    setSendCancellationEmail(e.target.checked);
                  }}
                />
              </div>
              {isCancelAppointmentReasonRequired && (
                <div className="column">
                  <SelectField
                    isRequired={true}
                    label="Reason for cancellation"
                    value={appointmentCanceledReason}
                    onChange={(e) => {
                      setFailureReason(null);
                      setAppointmentCanceledReason(e.target.value);
                    }}
                    options={APPOINTMENT_CANCELED_REASON_OPTIONS}
                  />
                </div>
              )}
            </div>
          )}
          {(isCreateAppointment || actionName === "change_appointment") && (
            <div className="columns is-variable is-1">
              <div className="column">
                <CheckboxField
                  label="Skip sending initial SMS to the client"
                  checked={skipInitialSMS}
                  onChange={() => setSkipInitialSMS(!skipInitialSMS)}
                />
              </div>
            </div>
          )}
        </div>
        <Tooltip
          text="Available timeslot colors notes, different colors represent the room number booked for that time slot: Room 1(blue), Room 2(green), Room 3(pink) and Room 4(purple). Yellow means this time is available, but no room is reserved"
          iconClassName="fa-solid fa-circle-question"
        />
        {(isAppointmentNotExistAndNotAPTRequests ||
          isAPTRequestAndAppointmentExist ||
          actionName === "change_appointment") && (
          <div className="column is-one-quarter">
            <div className="timeslots-container">
              <AppointmentTimeslot
                startTime={appointmentStartTime}
                duration={duration}
                selectStartTime={handleChangeAppointmentStartTime}
                availableTimeslots={
                  selectedPractitionerAvailabilities?.find(({ date }) => appointmentDate === date)?.availableTimeslots
                }
                bookedTimeslots={
                  selectedPractitionerAvailabilities?.find(({ date }) => appointmentDate === date)?.bookingTimeslots
                }
              />
            </div>
          </div>
        )}
      </div>

      <div className="columns is-variable is-1">
        <div className="column">
          {isCreateAppointment && <Button text="Create" isLoading={isLoading} onClick={handleSubmit} />}
          {isAppointmentExistAndIsNotAPTRequests && actionName === "add_cancel_appointment" && (
            <Button text="Cancel" isLoading={isLoading} onClick={handleDelete} />
          )}
          {actionName === "change_appointment" && (
            <Button text="Reschedule" isLoading={isLoading} onClick={handleChange} />
          )}
          <p className="has-text-danger">{failureReason}</p>
        </div>
      </div>
    </>
  );
};

export default AppointmentForm;
